import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Spinner, ButtonGroup } from 'react-bootstrap';
import { Home, Pencil, Trash2, Grid, List, Download } from 'lucide-react';
import { showSuccess, showError, showConfirm } from '../../../utils/notifications';
import { House, User } from '../../../services/types';
import housesService from '../../../services/housesService';
import usersService from '../../../services/usersService';
import { exportHousesToExcel } from '../../../utils/excelExportHouses';
import DataTable, { ColumnDef, ActionButton } from '../../../components/common/DataTable';
import { useUserData } from '../../../hooks';
import HousesListView from '../../../components/houses/HousesListView';
import { ERROR_MESSAGES, handleError } from '../../../utils/errorHandler';

type ViewMode = 'table' | 'list';

const Houses = () => {
    const { canEdit, canDelete, role } = useUserData();
    const [houses, setHouses] = useState<House[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingHouse, setEditingHouse] = useState<House | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('table');

    // Detect mobile and force list view
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 920;
            setIsMobile(mobile);
            // Force list view on mobile/tablet
            if (mobile) {
                setViewMode('list');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    type FormData = {
        name: string;
        color: string;
        value_per_service: string;
        observation: string;
        website: string;
        address: string;
        location: string;
        post_code: string;
        user_id: string;
    };

    const [formData, setFormData] = useState<FormData>({
        name: '',
        color: '',
        value_per_service: '',
        observation: '',
        website: '',
        address: '',
        location: '',
        post_code: '',
        user_id: '',
    });
    const [saving, setSaving] = useState(false);

    // Fetch houses and users on component mount
    useEffect(() => {
        fetchHouses();
        fetchUsers();
    }, []);

    /**
     * Fetch all houses from API
     */
    const fetchHouses = async () => {
        try {
            setLoading(true);
            const data = await housesService.getAll();
            setHouses(Array.isArray(data) ? data : []);
        } catch (err: any) {
            setHouses([]);
            showError('Error!', handleError(err, ERROR_MESSAGES.HOUSES_LOAD_FAILED));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetch all users for dropdown
     */
    const fetchUsers = async () => {
        try {
            const data = await usersService.getAll();
            setUsers(Array.isArray(data) ? data : []);
        } catch (err: any) {
            console.error('Error loading users:', err);
        }
    };

    /**
     * Open modal to add new house
     */
    const handleAdd = () => {
        setEditingHouse(null);
        setFormData({
            name: '',
            color: '#d89d37',
            value_per_service: '',
            observation: '',
            website: '',
            address: '',
            location: '',
            post_code: '',
            user_id: '',
        });
        setShowModal(true);
    };

    /**
     * Open modal to edit existing house
     */
    const handleEdit = (house: House) => {
        setEditingHouse(house);
        setFormData({
            name: house.name || '',
            color: house.color || '',
            value_per_service: house.value_per_service?.toString() || '',
            observation: house.observation || '',
            website: house.website || '',
            address: house.address,
            location: house.location,
            post_code: house.post_code,
            user_id: house.user_id || '',
        });
        setShowModal(true);
    };

    /**
     * Delete house
     */
    const handleDelete = async (publicId: string) => {
        const confirmed = await showConfirm(
            'Are you sure?',
            'You won\'t be able to revert this!',
            'Yes, delete it!',
            'Cancel'
        );

        if (!confirmed) {
            return;
        }

        try {
            await housesService.delete(publicId);
            setHouses(houses.filter(house => house.public_id !== publicId));
            showSuccess('Deleted!', 'House has been deleted successfully.');
        } catch (err: any) {
            showError('Error!', handleError(err, ERROR_MESSAGES.HOUSES_DELETE_FAILED));
        }
    };

    /**
     * Export houses to Excel
     */
    const handleDownloadExcel = () => {
        try {
            exportHousesToExcel(houses, users);
            showSuccess('Success!', 'Houses exported to Excel successfully.');
        } catch (err: any) {
            showError('Error!', 'Failed to export houses to Excel. Please try again.');
            console.error('Error exporting to Excel:', err);
        }
    };

    /**
     * Validate form inputs
     */
    const validateForm = (): boolean => {
        if (!formData.address.trim()) {
            showError('Validation Error', 'Address is required');
            return false;
        }

        if (!formData.location.trim()) {
            showError('Validation Error', 'Location is required');
            return false;
        }

        if (!formData.post_code.trim()) {
            showError('Validation Error', 'Post code is required');
            return false;
        }

        if (!formData.color.trim()) {
            showError('Validation Error', 'Color is required');
            return false;
        }

        if (!formData.user_id.trim()) {
            showError('Validation Error', 'User is required');
            return false;
        }

        return true;
    };

    /**
     * Submit form (create or update house)
     */
    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);

            const payload = {
                name: formData.name || null,
                color: formData.color,
                value_per_service: formData.value_per_service ? parseFloat(formData.value_per_service) : null,
                observation: formData.observation || null,
                website: formData.website || null,
                address: formData.address,
                location: formData.location,
                post_code: formData.post_code,
                user_id: formData.user_id,
            };

            if (editingHouse) {
                const updatedHouse = await housesService.update(editingHouse.public_id, payload);
                setHouses(houses.map(house =>
                    house.public_id === editingHouse.public_id ? updatedHouse : house
                ));
                showSuccess('Updated!', 'House has been updated successfully.');
            } else {
                const newHouse = await housesService.create(payload);
                setHouses([...houses, newHouse]);
                showSuccess('Created!', 'House has been created successfully.');
            }

            setShowModal(false);
        } catch (err: any) {
            showError('Error!', handleError(err, editingHouse ? ERROR_MESSAGES.HOUSES_UPDATE_FAILED : ERROR_MESSAGES.HOUSES_CREATE_FAILED));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Get user name by public_id
     */
    const getUserName = (userId?: string | null): string => {
        if (!userId) return 'N/A';
        const user = users.find(u => u.public_id === userId);
        return user ? user.nome : 'Unknown';
    };

    return (
        <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
            <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-between align-items-center'} mb-4`}>
                {!isMobile && <h4 className="mb-0" style={{ fontSize: '1.5rem' }}>Houses Management</h4>}
                <div className={`d-flex gap-2 ${isMobile ? 'w-100' : ''}`}>
                    {/* Super Admin only: Download Excel button */}
                    {role === 'Super Admin' && (
                        <Button
                            variant="outline-success"
                            onClick={handleDownloadExcel}
                            className={isMobile ? '' : ''}
                            title="Export houses to Excel"
                        >
                            <Download size={18} className={isMobile ? '' : 'me-2'} />
                            {!isMobile && 'Download Excel'}
                        </Button>
                    )}

                    {/* View Toggle */}
                    <ButtonGroup className={isMobile ? 'flex-grow-1' : ''}>
                        <Button
                            variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                            onClick={() => setViewMode('table')}
                            title={isMobile ? "Table view not available on mobile" : "Table View"}
                            className={isMobile ? 'flex-grow-1' : ''}
                            disabled={isMobile}
                            style={{
                                opacity: isMobile ? 0.6 : 1,
                                cursor: isMobile ? 'not-allowed' : 'pointer',
                                backgroundColor: isMobile ? '#e9ecef' : undefined,
                                borderColor: isMobile ? '#dee2e6' : undefined,
                                color: isMobile ? '#6c757d' : undefined
                            }}
                        >
                            <Grid size={18} />
                        </Button>
                        <Button
                            variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                            onClick={() => setViewMode('list')}
                            title="Card View"
                            className={isMobile ? 'flex-grow-1' : ''}
                        >
                            <List size={18} />
                        </Button>
                    </ButtonGroup>

                    {canEdit && (
                        <Button variant="primary" onClick={handleAdd} className={isMobile ? 'w-100' : ''}>
                            <Home size={18} className="me-2" />
                            {!isMobile && 'Add New House'}
                        </Button>
                    )}
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading houses...</p>
                    </Card.Body>
                </Card>
            ) : !Array.isArray(houses) || houses.length === 0 ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <p className="text-muted">No houses found</p>
                        {canEdit && (
                            <Button variant="primary" onClick={handleAdd}>
                                <Home size={18} className="me-2" />
                                Add First House
                            </Button>
                        )}
                    </Card.Body>
                </Card>
            ) : viewMode === 'list' ? (
                <HousesListView
                    houses={houses}
                    users={users}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                />
            ) : (
                <>
                    {(() => {
                        // Define table columns
                        const columns: ColumnDef<House>[] = [
                            {
                                key: 'name',
                                header: 'Name',
                                sortable: true,
                                render: (house) => (
                                    <div style={{ fontWeight: '500', color: '#111827' }}>
                                        {house.name || 'N/A'}
                                    </div>
                                ),
                            },
                            {
                                key: 'address',
                                header: 'Address',
                                sortable: true,
                                render: (house) => (
                                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                        {house.address}
                                    </div>
                                ),
                            },
                            {
                                key: 'location',
                                header: 'Location',
                                sortable: true,
                                render: (house) => (
                                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                        {house.location}
                                    </div>
                                ),
                            },
                            {
                                key: 'post_code',
                                header: 'Post Code',
                                sortable: true,
                                render: (house) => (
                                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                        {house.post_code}
                                    </div>
                                ),
                            },
                            {
                                key: 'user_id',
                                header: 'Owner',
                                sortable: true,
                                render: (house) => (
                                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                        {getUserName(house.user_id)}
                                    </div>
                                ),
                            },
                        ];

                        // Define table actions based on permissions
                        const actions: ActionButton<House>[] = [
                            ...(canEdit ? [{
                                label: 'Edit',
                                icon: <Pencil size={14} />,
                                variant: 'secondary' as const,
                                onClick: handleEdit,
                            }] : []),
                            ...(canDelete ? [{
                                label: 'Delete',
                                icon: <Trash2 size={14} />,
                                variant: 'danger' as const,
                                onClick: (house: House) => handleDelete(house.public_id),
                            }] : []),
                        ];

                        return (
                            <DataTable
                                columns={columns}
                                data={houses}
                                actions={actions}
                                onRowClick={canEdit ? handleEdit : undefined}
                                emptyMessage="No houses found"
                                keyExtractor={(house) => house.public_id}
                            />
                        );
                    })()}
                </>
            )}

            {/* Add/Edit Modal */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                scrollable
                size="lg"
                centered={!isMobile}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{editingHouse ? 'Edit House' : 'Add New House'}</Modal.Title>
                </Modal.Header>
                <Modal.Body
                    style={{
                        maxHeight: isMobile ? 'calc(100vh - 200px)' : 'calc(100vh - 250px)',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch'
                    }}
                >
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Enter house name"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                placeholder="Enter address"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Location <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.location}
                                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                placeholder="Enter location"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Post Code <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.post_code}
                                onChange={(e) => setFormData({ ...formData, post_code: e.target.value })}
                                placeholder="Enter post code"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Color Reference <span className="text-danger">*</span></Form.Label>
                            <div className="d-flex gap-2 align-items-center">
                                <Form.Control
                                    type="color"
                                    value={formData.color || '#d89d37'}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    style={{ width: '80px', height: '45px', cursor: 'pointer' }}
                                    title="Select house color"
                                    required
                                />
                                <Form.Control
                                    type="text"
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    placeholder="#d89d37"
                                    style={{ flex: 1 }}
                                    required
                                />
                            </div>
                            <Form.Text className="text-muted">
                                Select a color or enter a hex code (e.g., #FF5733)
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Value Per Service</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                value={formData.value_per_service}
                                onChange={(e) => setFormData({ ...formData, value_per_service: e.target.value })}
                                placeholder="Enter value per service"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Observation</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.observation}
                                onChange={(e) => setFormData({ ...formData, observation: e.target.value })}
                                placeholder="Enter any observations or notes"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Website</Form.Label>
                            <Form.Control
                                type="url"
                                value={formData.website}
                                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                placeholder="Enter website URL"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Owner <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                                value={formData.user_id}
                                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                                required
                            >
                                <option value="">Select owner</option>
                                {users.map((user) => (
                                    <option key={user.public_id} value={user.public_id}>
                                        {user.nome} ({user.email})
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer
                    style={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 10,
                        backgroundColor: '#fff'
                    }}
                >
                    <Button
                        variant="secondary"
                        onClick={() => setShowModal(false)}
                        disabled={saving}
                        style={{
                            opacity: saving ? 0.6 : 1,
                            cursor: saving ? 'not-allowed' : 'pointer',
                            backgroundColor: saving ? '#e9ecef' : undefined,
                            borderColor: saving ? '#dee2e6' : undefined,
                            color: saving ? '#6c757d' : undefined
                        }}
                    >
                        Cancel
                    </Button>
                    {canEdit && (
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={saving}
                            style={{
                                opacity: saving ? 0.6 : 1,
                                cursor: saving ? 'not-allowed' : 'pointer',
                                backgroundColor: saving ? '#6c757d' : undefined,
                                borderColor: saving ? '#6c757d' : undefined
                            }}
                        >
                            {saving ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Saving...
                                </>
                            ) : (
                                editingHouse ? 'Update' : 'Create'
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Houses;